import React, { useEffect, useState, useRef, useContext } from "react";
import { Box, Typography, List, ListItem, ListItemText, Avatar } from "@mui/material";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { startStream } from "./../../action/stream";
import { addSongAction } from "./../../action/songs";
import ApiService from "./../../services/api-service";
import { truncate } from "./../../constants/function";
import Music from "./../../assets/common/resources/default/Music.svg";
import { keyframes } from "@mui/system";
import { SocketContext } from "../../context/socket";
import cookieStorage from "../../services/cookie-storage";
import LiveStreamSnapshot from "./../LiveStreamSnapshot";

const SidebarRight = () => {
  const socket = useContext(SocketContext).Socket;

  // State to store songs, live streams, playlists, featured artist, etc.
  const [topSongs, setTopSongs] = useState([]);
  const [liveStreams, setLiveStreams] = useState([]);
  const [topPlaylists, setTopPlaylists] = useState([]);
  const [featuredArtist, setFeaturedArtist] = useState(null);

  const history = useHistory();
  const dispatch = useDispatch();

  // We'll track the current streams in a ref for real-time updates
  const streamRef = useRef([]);

  // Optional keyframe animation (not required for the core logic)
  const pulseAnimation = keyframes`
    0% { transform: scale(1); opacity: 0.5; background-color: darkred; }
    50% { transform: scale(1.3); opacity: 1; background-color: red; }
    100% { transform: scale(1); opacity: 0.5; background-color: darkred; }
  `;

  // ==============================
  //  useEffect: fetch initial data
  // ==============================
  useEffect(() => {
    getNewReleaseSongs();
    getLiveStreams();
    mostStreamedPlaylist();
    getFeaturedArtist();

    // Socket events for new or ended streams
    // Update live streams every 30 seconds
    const interval = setInterval(() => {
      getLiveStreams();
    }, 30000);

    if (socket) {
      socket.on("stream-joined", handleStreamJoined);
      socket.on("stream-ended", handleStreamEnded);
    }

    return () => {
      clearInterval(interval);
      if (socket) {
        socket.off("stream-joined", handleStreamJoined);
        socket.off("stream-ended", handleStreamEnded);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  // ==============================
  //  API calls
  // ==============================
  const getNewReleaseSongs = async () => {
    try {
      const response = await ApiService.MUSIC_NEW_RELEASE();
      if (response?.result?.length) {
        setTopSongs(response.result.slice(0, 4));
      } else {
        setTopSongs([]);
      }
    } catch (error) {
      console.error("Error fetching new release songs:", error);
      setTopSongs([]);
    }
  };

  const mostStreamedPlaylist = async () => {
    try {
      const response = await ApiService.PLAYLIST_STREAMED_LASTWEEK();
      const playlists = response?.result ? response.result.slice(0, 4) : [];
      setTopPlaylists(playlists);
    } catch (error) {
      console.error("Error fetching playlists:", error);
      setTopPlaylists([]);
    }
  };

  const getLiveStreams = async () => {
    try {
      const response = await ApiService.LIVESTREAM();
      console.log("Live Streams Response:", response);

      // Convert your stream objects into a simpler shape for UI
      const streams = response?.result
        ? response.result.slice(0, 4).map((stream) => ({
            id: stream.channelName,
            title: stream.title || "Live Stream",
            creatorId: stream.createdById || "",
            creator: `${stream.createdByDetails.firstName || ""} ${stream.createdByDetails.lastName || ""}`.trim(),
            thumbnail: stream.GIF?.filePath || stream.thumbnailImg?.filePath || Music,
          }))
        : [];

      console.log("Live Streams (parsed):", streams);
      setLiveStreams(streams);
      streamRef.current = streams;
    } catch (error) {
      console.error("Error fetching live streams:", error);
      setLiveStreams([]);
    }
  };

  const getFeaturedArtist = async () => {
    try {
      const response = await ApiService.FEATURE_ARTIST();
      if (response?.result && response.result.length > 0) {
        setFeaturedArtist(response.result[0]);
      } else {
        setFeaturedArtist(null);
      }
    } catch (error) {
      console.error("Error fetching featured artist:", error);
      setFeaturedArtist(null);
    }
  };

  // ==============================
  //  Socket event handlers
  // ==============================
  const handleStreamJoined = (streamData) => {
    if (!streamData || !streamData.livestreamData) return;
    const newStream = streamData.livestreamData;
    console.log("[socket stream-joined] streamData:", streamData);

    // If the new stream isn't in our list, add it
    if (!streamRef.current.some((stream) => stream.id === newStream.channelName)) {
      const updatedStreams = [
        {
          id: newStream.channelName,
          title: newStream.title || "Live Stream",
          creatorId: newStream.createdById || "",
          creator: newStream.createdBy || "Unknown Creator",
          thumbnail: newStream.GIF?.filePath || newStream.thumbnailImg?.filePath || Music,
        },
        ...streamRef.current,
      ];
      setLiveStreams(updatedStreams);
      streamRef.current = updatedStreams;
    }
  };

  const handleStreamEnded = (data) => {
    if (!data?.channelName) return;
    console.log("[socket stream-ended]", data.channelName);

    const updatedStreams = streamRef.current.filter((stream) => stream.id !== data.channelName);
    setLiveStreams(updatedStreams);
    streamRef.current = updatedStreams;
  };

  // ==============================
  //  handlePlayStream
  // ==============================
  const handlePlayStream = async (channelName) => {
    try {
      console.log("User wants to join existing stream =>", channelName);

      // (optional) fetch additional playlist info
      const playlistResponse = await ApiService.PLAYLIST_DETAIL({
        _id: channelName,
        pageNo: 1,
        pageSize: 1,
      });

      // Body for join
      const finalPayload = {
        channelName,
        platform: "hedera",
      };

      // Call your REST API to join
      const joinResp = await ApiService.LIVEJOIN(finalPayload);
      console.log("[handlePlayStream] After LIVEJOIN =>", joinResp);

      // We expect top-level fields like { token, channelName, ... }
      if (joinResp?.token && joinResp?.channelName) {
        console.log("[handlePlayStream] LIVEJOIN success =>", joinResp);

        // If we want to store playlist info in Redux
        if (playlistResponse?.result) {
          dispatch(startStream({ streamData: playlistResponse.result }));
        }

        // The route we want to push
        const newPath = `/app/LiveVideoStreaming/${channelName}`;
        console.log("[handlePlayStream] Navigating to =>", newPath);

        // Actually navigate
        history.push(newPath);
      } else {
        console.error("[handlePlayStream] LIVEJOIN failed:", joinResp);
      }
    } catch (error) {
      console.error("[handlePlayStream] Error joining stream:", error);
    }
  };

  // For normal audio songs
  const handlePlaySong = (musicItem) => {
    dispatch(addSongAction(musicItem));
  };

  // ==============================
  //  JSX
  // ==============================
  return (
    <Box
      sx={{
        backgroundColor: (theme) => theme.palette.background.paper,
        minHeight: "100vh",
        width: "100%",
        padding: "15px 15px 50px 15px",
        color: (theme) => theme.palette.text.primary,
        marginTop: "46px",
        overflowY: "auto",
        height: "calc(100vh - 46px)",
        borderRadius: "16px",
      }}
    >
      {/* NOW LIVE */}
      {liveStreams.length > 0 && (
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "12px", color: (theme) => theme.palette.text.primary }}>
              NOW LIVE
            </Typography>
            <Typography
              sx={{
                color: (theme) => theme.palette.text.primary,
                fontSize: "9px",
                textTransform: "none",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => {
                console.log("[SidebarRight] Going to => /app/live");
                history.push("/app/live");
              }}
            >
              Explore More
            </Typography>
          </Box>
          <List>
            {liveStreams.map((stream) => (
              <ListItem
                key={stream.id}
                sx={{
                  pl: 1.5,
                  py: 0.8,
                  borderRadius: "8px",
                backgroundColor: (theme) => theme.palette.action.hover,
                border: "1px solid",
                borderColor: (theme) => theme.palette.divider,
                boxShadow: "0px 1px 2px rgba(0,0,0,0.04)",
                  mb: 1,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "scale(1.02)",
                  boxShadow: (theme) => theme.shadows[2],
                  },
                }}
              >
                <Avatar
                  src={stream.thumbnail}
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "6px",
                    mr: 1.5,
                    cursor: "pointer",
                  }}
                  onClick={() => handlePlayStream(stream.id)}
                />
                <ListItemText
                  primary={truncate(stream.title, 12)}
                  secondary={stream.creator}
                primaryTypographyProps={{ fontSize: "12px", fontWeight: "bold", color: (theme) => theme.palette.text.primary }}
                secondaryTypographyProps={{ color: (theme) => theme.palette.text.secondary, fontSize: "10px" }}
                  onClick={() => handlePlayStream(stream.id)}
                />
                          {/* Pulsating red dot */}
          <Box
            sx={{
              position: "absolute",
              right: 10,
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "red",
              animation: `${pulseAnimation} 1.5s infinite`,
            }}
          />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* NEW RELEASES */}
      <Box mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "12px", color: (theme) => theme.palette.text.primary }}>
            NEW RELEASES
          </Typography>
          <Typography
            sx={{
              color: (theme) => theme.palette.text.primary,
              fontSize: "9px",
              textTransform: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => {
              console.log("[SidebarRight] Going to => /app/feature/NewRelease");
              history.push("/app/feature/NewRelease");
            }}
          >
            Explore More
          </Typography>
        </Box>
        <List>
          {topSongs.map((song) => (
            <ListItem
              key={song._id}
              sx={{
                pl: 1.5,
                py: 0.8,
                borderRadius: "8px",
                backgroundColor: (theme) => theme.palette.action.hover,
                border: "1px solid",
                borderColor: (theme) => theme.palette.divider,
                boxShadow: "0px 1px 2px rgba(0,0,0,0.04)",
                mb: 1,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: (theme) => theme.shadows[2],
                },
              }}
            >
              <Avatar
                src={song.thumbnailImg?.filePath}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "6px",
                  mr: 1.5,
                  cursor: "pointer",
                }}
                onClick={() => handlePlaySong(song)}
              />
              <ListItemText
                primary={truncate(song.title, 15)}
                secondary={song.artistData?.[0]?.fullName || "Unknown Artist"}
                primaryTypographyProps={{ fontSize: "12px", fontWeight: "bold", color: (theme) => theme.palette.text.primary }}
                secondaryTypographyProps={{ color: (theme) => theme.palette.text.secondary, fontSize: "10px" }}
                onClick={() => handlePlaySong(song)}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* TOP PLAYLISTS */}
      <Box mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "12px", color: (theme) => theme.palette.text.primary }}>
            TOP PLAYLISTS
          </Typography>
          <Typography
            sx={{
              color: (theme) => theme.palette.text.primary,
              fontSize: "9px",
              textTransform: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => {
              console.log("[SidebarRight] Going to => /app/feature/MostStreamedThisWeekStudio");
              history.push("/app/feature/MostStreamedThisWeekStudio");
            }}
          >
            Explore More
          </Typography>
        </Box>
        <List>
          {topPlaylists.map((playlist) => (
            <ListItem
              key={playlist._id}
              sx={{
                pl: 1.5,
                py: 0.8,
                borderRadius: "8px",
                backgroundColor: (theme) => theme.palette.action.hover,
                border: "1px solid",
                borderColor: (theme) => theme.palette.divider,
                boxShadow: "0px 1px 2px rgba(0,0,0,0.04)",
                mb: 1,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: (theme) => theme.shadows[2],
                },
              }}
            >
              <Avatar
                src={playlist.GIF?.filePath || playlist.thumbnailImg?.filePath || Music}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "6px",
                  mr: 1.5,
                  cursor: "pointer",
                }}
                onClick={() => {
                  console.log("[SidebarRight] Going to => /app/Music/", playlist._id);
                  history.push(`/app/Music/${playlist._id}`);
                }}
              />
              <ListItemText
                primary={truncate(playlist.title, 15)}
                secondary={playlist.createdBy || "Unknown Artist"}
                primaryTypographyProps={{ fontSize: "12px", fontWeight: "bold", color: (theme) => theme.palette.text.primary }}
                secondaryTypographyProps={{ color: (theme) => theme.palette.text.secondary, fontSize: "10px" }}
                onClick={() => {
                  console.log("[SidebarRight] Also going to => /app/Music/", playlist._id);
                  history.push(`/app/Music/${playlist._id}`);
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default SidebarRight;
